import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetRegistrationSummaryQuery as GetRegistrationSummary, TeamInput } from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import EditRegistrationBackendMutation from './EditRegistrationBackendMutation';
import FindOrAddTeamForm, { findOrAddTeamSchema } from '../../Team/FindOrAddTeamForm';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import GetRegistrationQuery from './GetRegistrationQuery';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import GetTeamQuery from '../../Team/GetTeamQuery';
import RegistrationInfo from './RegistrationInfo';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

type Registration = GetRegistrationSummary['registrationSummary']['registrations'][0];

const setTeamSchema = yup.object({
  team: findOrAddTeamSchema.nullable(),
});

export interface EditTeamFormProps {
  registration: Registration;
  onSuccess: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const EditTeamForm = ({
  registration, onSuccess, onCancel,
  refetchQueries = [GetParticipantDataQuery, GetRegistrationSummaryQuery, GetTeamQuery],
}: EditTeamFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      team: registration.team ? {
        team_type: { id: registration.team.team_type.id },
        id: registration.team.id,
      } as TeamInput : null,
      batch_update_team: true,
    },
  });

  const [
    editRegistration,
    { error, loading },
  ] = useMutation(
    EditRegistrationBackendMutation,
    {
      variables: {
        input: {
          id: registration.id,
          team: form.values.team ? {
            // Don't send captain.
            team_type: form.values.team.team_type,
            id: form.values.team.id,
            title: form.values.team.title,
          } : null,
          unset_team: !form.values.team,
          batch_update_team: form.values.batch_update_team,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.registration_saved'));
        onSuccess();
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: setTeamSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editRegistration()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('edit_team_membership')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('set_team_description')}
          </UI.Paragraph>
        </UI.Div>

        <RegistrationInfo registration={registration} />

        <UI.ServerErrors error={error} />

        <UI.FormGrid>
          <FindOrAddTeamForm
            form={form}
            team={registration.team}
            eventId={registration.event.id}
            captain={registration.participant}
            errors={errors}
          />
        </UI.FormGrid>

        {registration.purchase.batch_size > 1 && (
          <UI.InputGroup sc={{ valid: !errors.batch_update_team, touched: form.touched.batch_update_team }}>
            <UI.Checkbox {...form.checkbox('batch_update_team')}>
              {t('batch_update_team', { count: registration.purchase.batch_size })}
            </UI.Checkbox>
            <UI.ErrorMessages attribute={t('field')} errors={errors.batch_update_team} />
          </UI.InputGroup>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('confirm')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseEditTeamModalProps {
  registrationId: string;
  refetchQueries?: DocumentNode[];
}

const useEditTeamModal = ({
  registrationId, refetchQueries,
}: UseEditTeamModalProps) => {
  /**
   * Used to prevent state updates on an unmounted component.
   * When a registration is assigned away, the <RegistrationDetails /> component is unmounted.
   * If we don't check for this, React may throw an error.
   */
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  });

  const [open, setOpen] = useState(false);

  const { data } = useQuery(GetRegistrationQuery, {
    variables: {
      id: registrationId,
    },
    skip: !open,
  });

  const registration = data?.registration;

  const [doOpenModal, closeModal] = useModal(() => {
    const handleSuccess = () => {
      if (mountedRef.current) {
        closeModal();
      }
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        {!registration && <UI.PageLoader />}

        {registration && (
          <EditTeamForm
            registration={registration}
            refetchQueries={refetchQueries}
            onSuccess={handleSuccess}
            onCancel={closeModal}
          />
        )}
      </UI.Modal>
    );
  }, [registration, refetchQueries]);

  const openModal = () => {
    setOpen(true);
    doOpenModal();
  };

  return [openModal, closeModal];
};

export default useEditTeamModal;
